import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { CompanySelectionModal, CompanyInfoBox } from '../../components/forms';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { useCompanySelection } from '../../hooks/useCompanySelection';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialForm } from '../../types/psychosocial';

const PsychosocialCampaignFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { effectiveCompanyId } = useCompanyContext();
  const { modalOpen, selectedCompany, showInfoBox, openModal, handleSelect } = useCompanySelection({ isEditMode: false });

  const [forms, setForms] = useState<PsychosocialForm[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [formId, setFormId] = useState<number | ''>('');
  const [endDate, setEndDate] = useState('');

  const resolvedCompanyId = selectedCompany ? Number(selectedCompany.id) : effectiveCompanyId;

  const loadForms = useCallback(async (companyId?: number) => {
    setLoadingForms(true);
    try {
      const result = await psychosocialService.getForms({ companyId, limit: 100 });
      setForms(result.data);
    } catch {
      enqueueSnackbar('Erro ao carregar formulários', { variant: 'error' });
    } finally {
      setLoadingForms(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadForms(resolvedCompanyId ?? undefined);
    setFormId('');
  }, [resolvedCompanyId, loadForms]);

  const handleSubmit = async () => {
    if (!name.trim() || !formId) {
      enqueueSnackbar('Preencha o nome e selecione um formulário', { variant: 'warning' });
      return;
    }

    if (!resolvedCompanyId) {
      enqueueSnackbar('Selecione a empresa', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const campaign = await psychosocialService.createCampaign({
        companyId: resolvedCompanyId,
        formId: formId as number,
        name,
        description: description || undefined,
        endDate: endDate || undefined,
      });
      enqueueSnackbar('Campanha criada com sucesso', { variant: 'success' });
      navigate(`/psychosocial/campaigns/${campaign.id}`);
    } catch (err: unknown) {
      const message =
        err instanceof Error &&
        'response' in err &&
        typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
          ? (err as { response: { data: { error: string } } }).response.data.error
          : 'Erro ao criar campanha';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Psicossocial', path: '/psychosocial' },
        { label: 'Campanhas', path: '/psychosocial' },
        { label: 'Nova Campanha' },
      ]}
      title="Nova Campanha"
      subtitle="Preencha os dados da nova campanha"
    >
      {showInfoBox && selectedCompany && (
        <CompanyInfoBox
          companyName={selectedCompany.name}
          onEdit={openModal}
        />
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome da Campanha"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              placeholder="Ex: Avaliação Psicossocial 2026-Q1"
            />
            <TextField
              label="Descrição"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              select
              label="Formulário"
              value={formId}
              onChange={(e) => setFormId(Number(e.target.value))}
              required
              fullWidth
              disabled={loadingForms || !resolvedCompanyId}
              helperText={!resolvedCompanyId ? 'Selecione uma empresa primeiro' : undefined}
            >
              {forms.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name} ({f.baseTemplate?.name || 'Custom'})
                </MenuItem>
              ))}
            </TextField>
            {!loadingForms && resolvedCompanyId && forms.length === 0 && (
              <Alert
                severity="warning"
                action={
                  <RouteActionButton
                    para="/psychosocial/forms/new"
                    exige={pode('psychosocial_form.create')}
                    color="inherit"
                    size="small"
                  >
                    Criar formulário
                  </RouteActionButton>
                }
              >
                Nenhum formulário cadastrado para esta empresa.
              </Alert>
            )}
            <TextField
              label="Data Limite"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
            <Button onClick={() => navigate('/psychosocial')}>Cancelar</Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Criando...' : 'Criar Campanha'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <CompanySelectionModal
        open={modalOpen}
        onSelect={handleSelect}
      />
    </PageShell>
  );
};

export default PsychosocialCampaignFormPage;
